import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import { requestPasswordReset } from '../services/user-service';
import { useAuth } from '../context/AuthContext';

const ForgotPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!email) e.email = 'Email is required';
        if (email.length > 100) e.email = 'Email must be 100 characters or fewer';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();

        if (!validate()) return;

        setLoading(true);
        try {
            await requestPasswordReset(email);
            navigate('/confirm/password-reset', {
                state: {
                    email: email,
                    message: "If an account exists for this email, we've sent a verification code.",
                },
            });
        } catch {
            navigate('/confirm/password-reset', {
                state: {
                    email,
                    message: "If an account exists for this email, we've sent a verification code.",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    if (isAuthenticated) return <Navigate to="/dashboard" />;

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <NavBar />
            <div className="flex-grow mt-16">
                <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
                    <h2 className="text-center text-2xl font-semibold text-crumbblue">Forgot Password</h2>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="email-input"
                            />
                            {errors.email && (
                                <div className="text-red-600 text-sm mt-1" data-testid="email-error">
                                    {errors.email}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-md bg-crumbblue p-2 font-semibold text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="submit-button"
                        >
                            {loading ? 'Sending...' : 'Send verification code'}
                        </button>

                        <div className="text-center">
                            <Link to="/login" className="text-crumbblue hover:underline text-sm font-medium">
                                Back to login
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ForgotPasswordPage;